import * as Popover from '@radix-ui/react-popover';

import { ReactNode, useState } from 'react';

import { FeedbackChip } from '@/components/FeedbackChips';
import { FeedbackConfigForm } from '@/components/FeedbackConfig/FeedbackConfigForm';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/Table';
import { Button } from '@/design-system';
import { usePermissions } from '@/hooks/usePermissions.tsx';
import { useFeedbackConfigs } from '@/hooks/useSwr';
import { cn } from '@/utils/tailwind';

function FeedbackConfigNewButton(props: { children: ReactNode }) {
  const [open, setOpen] = useState(false);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>{props.children}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="end" sideOffset={8} className="relative z-10">
          <div className="max-w-[420px] rounded-lg border border-secondary bg-popover p-4 shadow-lg">
            <FeedbackConfigForm onSuccess={() => setOpen(false)} />
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

const pluralRules = new Intl.PluralRules('en-US');

export function OrganizationFeedbackConfigs() {
  const feedbackConfigs = useFeedbackConfigs();
  const { authorize } = usePermissions();

  const nFeedbacks = feedbackConfigs.data?.length ?? 0;

  return (
    <div className="flex flex-col gap-4 overflow-auto">
      <div className="flex items-start gap-1">
        <div className="flex flex-grow flex-col gap-1">
          <h2 className={cn('font-semibold', 'text-lg')}>Feedback Tags</h2>
          <p className="text-sm text-tertiary">
            Create and organize feedback tags for this tenant
          </p>
        </div>
        {authorize('feedback:create') && (
          <FeedbackConfigNewButton>
            <Button size="md">New tag</Button>
          </FeedbackConfigNewButton>
        )}
      </div>

      <div className="flex flex-col items-stretch rounded-lg border border-secondary">
        <div className="px-5 py-4">
          {
            {
              one: `1 feedback tag`,
              two: `${nFeedbacks} feedback tags`,
              few: `${nFeedbacks} feedback tags`,
              other: `${nFeedbacks} feedback tags`,
            }[pluralRules.select(nFeedbacks)]
          }
        </div>

        {nFeedbacks > 0 && (
          <div className="overflow-hidden border-t border-secondary">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="h-10 pl-5">Feedback</TableHead>
                  <TableHead className="h-10">Type</TableHead>
                  <TableHead className="h-10">Values</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {feedbackConfigs.data?.map((i) => (
                  <TableRow key={i.feedback_key}>
                    <TableCell className="px-2 py-3 pl-5">
                      <div className="flex items-center justify-start">
                        <FeedbackChip
                          feedback_key={i.feedback_key}
                          allowTruncation={true}
                        />
                      </div>
                    </TableCell>
                    <TableCell className="py-3">
                      <span className="text-sm capitalize text-tertiary">
                        {i.feedback_config.type}
                      </span>
                    </TableCell>
                    <TableCell className="py-3">
                      <div className="flex flex-wrap items-center gap-1">
                        {i.feedback_config.type === 'categorical' && (
                          <>
                            {i.feedback_config.categories?.map(
                              (category, index) => (
                                <div
                                  key={index}
                                  className="rounded-md border border-secondary bg-background px-1.5 text-sm text-tertiary"
                                >
                                  {category.label ?? category.value}
                                </div>
                              )
                            )}
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
